import React, { useEffect } from 'react';
import {
  Card,
  Flexbox,
  Icon,
  PageWrapper,
  Select,
  Switch,
  Tooltip,
  Typography,
} from '@hiiretail/synergy-ui';
import { useGetLoginConfig } from '../IdentityProviders/hooks/useGetLoginConfig';
import { CopyToClipboard } from '../../common/components/CopyToClipboard/CopyToClipboard';
import { useGetTenantAlias } from '../../common/hooks/useGetTenantAlias/useGetTenantAlias';
import { InlineHint } from '../../common/components/InlineHint/InlineHint';
import { AccessControl } from '../../common';
import { useSetDefaultLocale } from './hooks/useSetDefaultLocale';
import { Option } from '@hiiretail/synergy-ui/dist/types/components/inputs/Select/Select.types';
import { useNotifications } from '../../common/notifications/Notifications';
import { useSetPrimaryConnectionId } from './hooks/useSetPrimaryConnectionId';
import { useBreadcrumbsActions } from '../../common/components/Breadcrumbs/context/useBreadcrumbsActions';
import { RefreshButton } from '../../common/components/Breadcrumbs/buttons/RefreshButton/RefreshButton';
import { LOCALES } from '../../common/utils/locales';
import { useQueryClient } from 'react-query';
import { useToggleAnonymousLogin } from './hooks/useToggleAnonymousLogin';
import { TIMEZONES } from '../../common/utils/timezones';
import { useSetDefaultTimezone } from './hooks/useSetDefaultTimezone';

export const MyTenant: React.FC = () => {
  const { data: loginConfig, isFetching, error, refetch: refetchLoginConfig } = useGetLoginConfig({
    keepPreviousData: true,
  });
  const alias = useGetTenantAlias();
  const queryClient = useQueryClient();
  const { showNotification } = useNotifications();
  const { updateActions } = useBreadcrumbsActions();

  useEffect(() => {
    updateActions(
      <RefreshButton onClick={() => {
        queryClient.resetQueries(['loginConfig']).then();
      }} permission={''} />,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const {
    isLoading: isSettingDefaultLocale,
    mutateAsync: setDefaultLocale,
  } = useSetDefaultLocale(
    {
      onSuccess: () => {
        showNotification('Default language updated successfully');
        refetchLoginConfig().then();
      },
      onError: () => {
        showNotification('An error occurred when updating default language');
        refetchLoginConfig().then();
      },
    },
  );

  const {
    isLoading: isSettingDefaultTimezone,
    mutate: setDefaultTimezone,
  } = useSetDefaultTimezone(
    {
      onSuccess: () => {
        showNotification('Default timezone updated successfully');
        refetchLoginConfig().then();
      },
      onError: () => {
        showNotification('An error occurred when updating default timezone');
        refetchLoginConfig().then();
      },
    },
  );

  const {
    isLoading: isSettingPrimaryConnectionId,
    mutate: setPrimaryConnectionId,
  } = useSetPrimaryConnectionId(
    {
      onSuccess: () => {
        showNotification('Primary ID provider updated successfully');
        refetchLoginConfig().then();
      },
      onError: () => {
        showNotification('An error occurred when updating primary ID provider');
        refetchLoginConfig().then();
      },
    },
  );

  const {
    isLoading: isTogglingAnonymousLogin,
    mutate: toggleAnonymousLogin,
  } = useToggleAnonymousLogin(
    {
      onSuccess: () => {
        showNotification('Anonymous login settings for Shoppers updated');
        refetchLoginConfig().then();
      },
      onError: () => {
        showNotification('An error occurred when updating anonymous login settings for Shoppers');
        refetchLoginConfig().then();
      },
    },
  );

  const handleLanguageSelectorChange = (option: Option | null) => {
    if (option) {
      setDefaultLocale({
        defaultLocale: option.value as string,
      })
        .then(() => localStorage.setItem('defaultLocale', option.value as string))
        .catch(() => {return});
    } else {
      setDefaultLocale({})
        .then(() => localStorage.setItem('defaultLocale', ''))
        .catch(() => {return});
    }
  };

  const handleTimezoneSelectorChange = (option: Option | null) => {
    if (option) {
      setDefaultTimezone({
        defaultTimezone: option.value as string
        }
      );
    } else {
      setDefaultTimezone({});
    }
  };

  const handlePrimaryIdProviderSelectorChange = (option: Option | null) => {
    if (option) {
      setPrimaryConnectionId({
        connectionId: option.value as string,
      });
    } else {
      setPrimaryConnectionId({});
    }
  };

  const handleAnonymousLoginSwitchChange = (value: boolean) => {
    toggleAnonymousLogin({
      anonymousShoppersLoginDisabled: !value,
    });
  };

  const getPrimaryIdProviderOptions = (): Option[] => {
    if (loginConfig) {
      if (loginConfig.primaryConnectionId) {
        return loginConfig.providers
          .filter((provider) => provider.connectionId !== loginConfig.primaryConnectionId)
          .map((provider) => ({
            label: `${provider.connectionId} ${provider.name && `(${provider.name})`}`,
            value: provider.connectionId,
          }));
      } else {
        return loginConfig.providers
          .map((provider) => ({
            label: `${provider.connectionId} ${provider.name && `(${provider.name})`}`,
            value: provider.connectionId,
          }));
      }
    }
    return [];
  };

  return (
    //@ts-ignore
    <PageWrapper isFetching={isFetching && !loginConfig} fetchError={error}>
      {
        loginConfig && (
          <Flexbox container direction="column" gutter={4}>
            <Card width="100%" header={`Configure ${loginConfig.tenantName}`}>
              <Flexbox container gutter={2} alignItems="flex-start">
                <Flexbox item>
                  <Icon.Info size="md" color={() => '#6E33A9'} />
                </Flexbox>
                <Flexbox item container>
                  <Typography variant="caption" color={() => '#666666'}>
                    A Hii Retail Tenant represents an isolated resource boundary on the
                    platform.<br />
                    A Tenant can manage their resources but cannot access another Tenant's resources
                    in any way.
                  </Typography>
                </Flexbox>
              </Flexbox>
            </Card>
            <Flexbox container gutter={4}>
              <Card width="50%" subHeader="Information">
                <Flexbox container direction="column" mb={5}>
                  <Flexbox item container alignItems="center" gutter={5}>
                    <Flexbox item>
                      <Typography variant="body2" weight="bold">ID</Typography>
                    </Flexbox>
                  </Flexbox>
                  <Flexbox item>
                    <CopyToClipboard text={loginConfig.tenantId} variant="body2" />
                  </Flexbox>
                </Flexbox>
                <Flexbox container direction="column" mb={5}>
                  <Flexbox item container alignItems="center" gutter={5}>
                    <Flexbox item>
                      <Typography variant="body2" weight="bold">Alias</Typography>
                    </Flexbox>
                    <Flexbox item>
                      <InlineHint placement="right">
                        <Typography variant="body2" weight="bold">
                          Tenant alias
                        </Typography>
                        <Typography variant="caption">
                          The alias is used in the URL for accessing the tenant's applications.
                          <br />
                          Ie: {'<tenant-alias>.hiiretail.com'}
                        </Typography>
                      </InlineHint>
                    </Flexbox>
                  </Flexbox>
                  <Flexbox item>
                    <CopyToClipboard text={alias} variant="body2" />
                  </Flexbox>
                </Flexbox>
                {
                  loginConfig.staffTenantId && (
                    <Flexbox container direction="column" mb={5}>
                      <Flexbox item container alignItems="center" gutter={5}>
                        <Flexbox item>
                          <Typography variant="body2" weight="bold">Staff tenant ID</Typography>
                        </Flexbox>
                        <Flexbox item>
                          <InlineHint placement="right">
                            <Typography variant="body2" weight="bold">
                              Staff tenant ID
                            </Typography>
                            <Typography variant="caption">
                              Unique tenant identifier for Staff users. Staff user represents
                              tenant's employee.<br />
                              This type of user can perform some administrative work for the tenant
                              if given the right permissions.<br />
                              Staff users can log in at [tenant-alias].hiiretail.com
                            </Typography>
                          </InlineHint>
                        </Flexbox>
                      </Flexbox>
                      <Flexbox item>
                        <CopyToClipboard text={loginConfig.staffTenantId} variant="body2" />
                      </Flexbox>
                    </Flexbox>
                  )
                }
                {
                  loginConfig.shopperTenantId && (
                    <Flexbox container direction="column" mb={5}>
                      <Flexbox item container alignItems="center" gutter={5}>
                        <Flexbox item>
                          <Typography variant="body2" weight="bold">Shopper tenant ID</Typography>
                        </Flexbox>
                        <Flexbox item>
                          <InlineHint placement="right">
                            <Typography variant="body2" weight="bold">
                              Shopper tenant ID
                            </Typography>
                            <Typography variant="caption">
                              Unique tenant identifier for Shopper users<br />
                              Represent a customer of a tenant. And is a user of other applications
                            </Typography>
                          </InlineHint>
                        </Flexbox>
                      </Flexbox>
                      <Flexbox item>
                        <CopyToClipboard text={loginConfig.shopperTenantId} variant="body2" />
                      </Flexbox>
                    </Flexbox>
                  )
                }
              </Card>
              <Card width="50%" subHeader="Configuration">

                <Flexbox container direction="column" mb={5}>
                  <Flexbox item container alignItems="center" gutter={5} mb={1}>
                    <Flexbox item>
                      <Typography variant="body2" weight="bold">Default language</Typography>
                    </Flexbox>
                    <Flexbox item>
                      <InlineHint placement="right">
                        <Typography variant="body2" weight="bold">
                          Default language
                        </Typography>
                        <Typography variant="caption">
                          Select default language for your tenant.
                          <br />
                          Default language will be applied to all applications.
                        </Typography>
                      </InlineHint>
                    </Flexbox>
                  </Flexbox>
                  <Flexbox item>
                    <AccessControl permission="iam.tenant.update" renderIfNoAccess={
                      <Tooltip title={
                        <Flexbox
                          container
                          direction="column"
                          item
                          flex="1 1 auto"
                          alignItems="center"
                          justifyContent="center"
                          gutter={1}
                        >
                          <Flexbox item>
                            <Icon.WarningTriangle size="lg" color={() => 'rgb(244, 67, 54)'} />
                          </Flexbox>
                          <Flexbox item>
                            <Typography variant="caption" color={() => 'white'}>
                              You don't have permissions to change default language
                            </Typography>
                          </Flexbox>
                          <Flexbox item>
                            <Typography variant="caption" color={() => 'white'}>
                              Required permissions:{' '}
                              {/*@ts-ignore*/}
                              <Typography variant="caption" weight="bold">
                                iam.tenant.update
                              </Typography>
                            </Typography>
                          </Flexbox>
                        </Flexbox>
                      }>
                        <Select disabled isClearable options={[]}
                                name="language-select" label="" hideLabel />
                      </Tooltip>
                    }>
                      <Select isSearchable placeholder="Not set"
                              initialValue={loginConfig.defaultLocale ? {
                                label: LOCALES[loginConfig.defaultLocale],
                                value: loginConfig.defaultLocale,
                              } : undefined}
                              disabled={isSettingDefaultLocale}
                              options={
                                Object.entries(LOCALES).map(([code, name]) => ({
                                  label: name,
                                  value: code,
                                }))
                              } name="language-select" onChange={handleLanguageSelectorChange}
                              label=""
                              hideLabel />
                    </AccessControl>
                  </Flexbox>
                </Flexbox>

                <Flexbox container direction="column" mb={5}>
                  <Flexbox item container alignItems="center" gutter={5} mb={1}>
                    <Flexbox item>
                      <Typography variant="body2" weight="bold">Default timezone</Typography>
                    </Flexbox>
                    <Flexbox item>
                      <InlineHint placement="right">
                        <Typography variant="body2" weight="bold">
                          Default timezone
                        </Typography>
                        <Typography variant="caption">
                          Select default timezone for your tenant.
                          <br />
                          Default timezone will be applied to all applications.
                        </Typography>
                      </InlineHint>
                    </Flexbox>
                  </Flexbox>
                  <Flexbox item>
                    <AccessControl permission="iam.tenant.update" renderIfNoAccess={
                      <Tooltip title={
                        <Flexbox
                          container
                          direction="column"
                          item
                          flex="1 1 auto"
                          alignItems="center"
                          justifyContent="center"
                          gutter={1}
                        >
                          <Flexbox item>
                            <Icon.WarningTriangle size="lg" color={() => 'rgb(244, 67, 54)'} />
                          </Flexbox>
                          <Flexbox item>
                            <Typography variant="caption" color={() => 'white'}>
                              You don't have permissions to change default timezone
                            </Typography>
                          </Flexbox>
                          <Flexbox item>
                            <Typography variant="caption" color={() => 'white'}>
                              Required permissions:{' '}
                              {/*@ts-ignore*/}
                              <Typography variant="caption" weight="bold">
                                iam.tenant.update
                              </Typography>
                            </Typography>
                          </Flexbox>
                        </Flexbox>
                      }>
                        <Select disabled isClearable options={[]}
                                name="timezone-select" label="" hideLabel />
                      </Tooltip>
                    }>
                      <Select isSearchable placeholder="Not set"
                              initialValue={loginConfig.defaultTimezone ? {
                                label: loginConfig.defaultTimezone,
                                value: loginConfig.defaultTimezone,
                              } : undefined}
                              disabled={isSettingDefaultTimezone}
                              options={
                                TIMEZONES.map((timezone) => ({
                                  label: timezone,
                                  value: timezone,
                                }))
                              } name="timezone-select" onChange={handleTimezoneSelectorChange}
                              label=""
                              hideLabel />
                    </AccessControl>
                  </Flexbox>
                </Flexbox>

                <Flexbox container direction="column" mb={5}>
                  <Flexbox item container alignItems="center" gutter={5} mb={1}>
                    <Flexbox item>
                      <Typography variant="body2" weight="bold">Primary ID provider</Typography>
                    </Flexbox>
                    <Flexbox item>
                      <InlineHint placement="right">
                        <Typography variant="body2" weight="bold">
                          Primary identity provider
                        </Typography>
                        <Typography variant="caption">
                          Selected provider will be automatically used for logging in to Hii Retail.
                          <br />
                          Allows to skip Hii Retail login screen and go directly to the login screen
                          of the provider
                        </Typography>
                      </InlineHint>
                    </Flexbox>
                  </Flexbox>
                  <Flexbox item>
                    <AccessControl permission="iam.tenant.update" renderIfNoAccess={
                      <Tooltip title={
                        <Flexbox
                          container
                          direction="column"
                          item
                          flex="1 1 auto"
                          alignItems="center"
                          justifyContent="center"
                          gutter={1}
                        >
                          <Flexbox item>
                            <Icon.WarningTriangle size="lg" color={() => 'rgb(244, 67, 54)'} />
                          </Flexbox>
                          <Flexbox item>
                            <Typography variant="caption" color={() => 'white'}>
                              You don't have permissions to change primary ID provider
                            </Typography>
                          </Flexbox>
                          <Flexbox item>
                            <Typography variant="caption" color={() => 'white'}>
                              Required permissions:{' '}
                              {/*@ts-ignore*/}
                              <Typography variant="caption" weight="bold">
                                iam.tenant.update
                              </Typography>
                            </Typography>
                          </Flexbox>
                        </Flexbox>
                      }>
                        <Select disabled isSearchable={false} isClearable={false} options={[]}
                                name="language-select" label="" hideLabel />
                      </Tooltip>
                    }>
                      <Select isClearable={true}
                              initialValue={loginConfig.primaryConnectionId ? {
                                label: loginConfig.primaryConnectionId,
                                value: loginConfig.primaryConnectionId,
                              } : undefined}
                              placeholder="Not set"
                              disabled={isSettingPrimaryConnectionId}
                              options={getPrimaryIdProviderOptions()} name="language-select"
                              onChange={handlePrimaryIdProviderSelectorChange}
                              label=""
                              hideLabel />
                    </AccessControl>
                  </Flexbox>
                </Flexbox>

                <Flexbox container direction="column">
                  <Flexbox item container alignItems="center" gutter={5} mb={1}>
                    <Flexbox item>
                      <Typography variant="body2" weight="bold">Shoppers anonymous
                        login</Typography>
                    </Flexbox>
                    <Flexbox item>
                      <InlineHint placement="right">
                        <Typography variant="body2" weight="bold">
                          Shoppers anonymous login
                        </Typography>
                        <Typography variant="caption">
                          Allow logging in using temporary anonymous accounts.
                          <br />
                          These temporary anonymous accounts can be used to allow users who haven't
                          yet signed up to your app to work with data protected by security rules.
                        </Typography>
                      </InlineHint>
                    </Flexbox>
                  </Flexbox>
                  <Flexbox item>
                    <AccessControl permission="iam.tenant.update" renderIfNoAccess={
                      <Tooltip title={
                        <Flexbox
                          direction="column"
                          container
                          item
                          justifyContent="center"
                          flex="1 1 auto"
                          alignItems="center"
                          gutter={1}
                        >
                          <Flexbox item>
                            <Icon.WarningTriangle color={() => 'rgb(244, 67, 54)'} size="lg" />
                          </Flexbox>
                          <Flexbox item>
                            <Typography color={() => 'white'} variant="caption">
                              You don't have permissions to change primary ID provider
                            </Typography>
                          </Flexbox>
                          <Flexbox item>
                            <Typography color={() => 'white'} variant="caption">
                              Required permissions:{' '}
                              {/*@ts-ignore*/}
                              <Typography variant="caption" weight="bold">
                                iam.tenant.update
                              </Typography>
                            </Typography>
                          </Flexbox>
                        </Flexbox>
                      }>
                        <Switch name="anonymous-login" disabled={isTogglingAnonymousLogin}
                                label={loginConfig.anonymousShoppersLoginDisabled ? 'Disabled' : 'Enabled'}
                                defaultChecked={!loginConfig.anonymousShoppersLoginDisabled} />
                      </Tooltip>
                    }>
                      <Flexbox container>
                        <Flexbox item>
                          <Switch name="anonymous-login" disabled={isTogglingAnonymousLogin}
                                        label={loginConfig.anonymousShoppersLoginDisabled ? 'Disabled' : 'Enabled'}
                                        onChange={(e) =>
                                          handleAnonymousLoginSwitchChange((e.target as HTMLInputElement).checked as boolean)}
                                        defaultChecked={!loginConfig.anonymousShoppersLoginDisabled} />
                        </Flexbox>
                      </Flexbox>
                    </AccessControl>
                  </Flexbox>
                </Flexbox>

              </Card>
            </Flexbox>
          </Flexbox>
        )
      }
    </PageWrapper>
  );
};
